import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, CheckCircle2, Eye, EyeOff, KeyRound, Mail, User } from 'lucide-react'
import { forwardRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { ApiRequestError, setAccessToken } from '@/api/client'
import { authApi } from '@/api/endpoints'
import { TrcArrowLogo } from '@/components/ui'
import { useAuth } from '@/features/auth/AuthContext'

// ── Schemas ──────────────────────────────────────────────────────────────────

const loginSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'Minimum 8 characters'),
})
const signupSchema = z.object({
  full_name: z.string().min(2, 'Enter your full name'),
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'Minimum 8 characters'),
  confirm: z.string(),
}).refine((d) => d.password === d.confirm, {
  message: "Passwords don't match",
  path: ['confirm'],
})
const forgotSchema = z.object({ email: z.string().email('Enter a valid email') })
const resetSchema = z.object({
  token: z.string().min(10, 'Paste the token from your reset email'),
  new_password: z.string().min(8, 'Minimum 8 characters'),
})

type Tab = 'login' | 'signup' | 'forgot' | 'reset'

// ── Sub-components ────────────────────────────────────────────────────────────

const PwInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { error?: string }>(
  ({ error, ...props }, ref) => {
    const [show, setShow] = useState(false)
    return (
      <div className="relative">
        <input
          {...props}
          ref={ref}
          type={show ? 'text' : 'password'}
          className={`w-full rounded-xl border-[1.5px] bg-white px-3.5 py-2.5 pr-11 text-[13px] text-slate-900 placeholder:text-slate-400 outline-none transition duration-150 focus:border-emerald-600 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 ${
            error ? 'border-[#c0392b]' : 'border-slate-200'
          }`}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow((s) => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900 transition"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
        {error && <p className="mt-1 text-[11px] font-medium text-[#c0392b]">{error}</p>}
      </div>
    )
  },
)
PwInput.displayName = 'PwInput'

const TextInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { error?: string }>(
  ({ error, ...props }, ref) => {
    return (
      <div>
        <input
          {...props}
          ref={ref}
          className={`w-full rounded-xl border-[1.5px] bg-white px-3.5 py-2.5 text-[13px] text-slate-900 placeholder:text-slate-400 outline-none transition duration-150 focus:border-emerald-600 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 ${
            error ? 'border-[#c0392b]' : 'border-slate-200'
          }`}
        />
        {error && <p className="mt-1 text-[11px] font-medium text-[#c0392b]">{error}</p>}
      </div>
    )
  },
)
TextInput.displayName = 'TextInput'

function SubmitBtn({ loading, children }: { loading?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="submit"
      disabled={loading}
      style={{
        background: 'var(--gradient)',
        boxShadow: '0 2px 8px rgba(5,55,54,0.2)',
      }}
      className="flex w-full items-center justify-center gap-2 rounded-xl py-3 text-[13.5px] font-bold text-[#dbf3d0] transition-all hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(5,55,54,0.3)] active:translate-y-0 active:scale-[0.98] disabled:opacity-50 disabled:hover:translate-y-0"
    >
      {loading ? (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
      ) : (
        children
      )}
    </button>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('login')
  const [formError, setFormError] = useState<string | null>(null)
  const [resetToken, setResetToken] = useState<string>('')
  const [resetDone, setResetDone] = useState(false)

  const clearError = () => setFormError(null)

  // ── Login form
  const loginForm = useForm({
    resolver: zodResolver(loginSchema),
    mode: 'onTouched',
    defaultValues: { email: '', password: '' },
  })
  const onLogin = loginForm.handleSubmit(async (v) => {
    clearError()
    try {
      await login(v.email, v.password)
      navigate('/', { replace: true })
    } catch (e) {
      setFormError(e instanceof ApiRequestError ? e.message : 'Unable to sign in.')
    }
  })

  // ── Signup form
  const signupForm = useForm({
    resolver: zodResolver(signupSchema),
    mode: 'onTouched',
    defaultValues: { full_name: '', email: '', password: '', confirm: '' },
  })
  const onSignup = signupForm.handleSubmit(async (v) => {
    clearError()
    try {
      const result = await authApi.register(v.full_name, v.email, v.password)
      setAccessToken(result.token.access_token)
      await login(v.email, v.password)
      navigate('/', { replace: true })
    } catch (e) {
      setFormError(e instanceof ApiRequestError ? e.message : 'Registration failed.')
    }
  })

  // ── Forgot password form
  const forgotForm = useForm({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: '' },
  })
  const onForgot = forgotForm.handleSubmit(async (v) => {
    clearError()
    try {
      const res = await authApi.forgotPassword(v.email)
      if (res.reset_token) {
        setResetToken(res.reset_token)
        setTab('reset')
      } else {
        setFormError('No account found with that email.')
      }
    } catch (e) {
      setFormError(e instanceof ApiRequestError ? e.message : 'Request failed.')
    }
  })

  // ── Reset password form
  const resetForm = useForm({
    resolver: zodResolver(resetSchema),
    defaultValues: { token: resetToken, new_password: '' },
  })
  const onReset = resetForm.handleSubmit(async (v) => {
    clearError()
    try {
      const result = await authApi.resetPassword(v.token, v.new_password)
      setAccessToken(result.token.access_token)
      setResetDone(true)
      setTimeout(() => navigate('/', { replace: true }), 1800)
    } catch (e) {
      setFormError(e instanceof ApiRequestError ? e.message : 'Reset failed.')
    }
  })

  const switchTab = (t: Tab) => {
    setTab(t)
    clearError()
  }

  return (
    <div
      style={{ background: 'var(--gradient)' }}
      className="relative flex min-h-dvh flex-col items-center justify-center overflow-hidden px-4 py-8"
    >
      {/* ── Pattern Overlay ── */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'radial-gradient(#6ebe48 1.5px, transparent 1.5px), radial-gradient(#6ebe48 1.5px, transparent 1.5px)',
          backgroundSize: '24px 24px',
          backgroundPosition: '0 0, 12px 12px',
        }}
      />

      <div className="relative w-full max-w-sm animate-slide-up z-10">
        {/* Logo and Brand */}
        <div className="mb-6 text-center">
          <div className="relative mx-auto mb-3 inline-flex">
            <div
              style={{
                boxShadow: '0 8px 24px rgba(5,55,54,0.3)',
                border: '2px solid rgba(194, 232, 176, 0.4)',
              }}
              className="flex h-[68px] w-[68px] items-center justify-center rounded-[18px] bg-white p-2.5 overflow-hidden"
            >
              <TrcArrowLogo size={38} />
            </div>
          </div>
          <h1 className="text-[22px] font-black tracking-[-0.5px] text-white">TRC Asset360</h1>
          <p className="text-[12px] font-bold uppercase tracking-[0.2px] text-[#6ebe48]">
            Enterprise Asset Management
          </p>
        </div>

        {/* ── Card ── */}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.97)',
            borderRadius: '20px',
            boxShadow: 'var(--sh-xl)',
            border: '1px solid rgba(110, 190, 72, 0.25)',
          }}
          className="p-6 backdrop-blur-[12px]"
        >
          {/* ── Tab Bar ── */}
          {(tab === 'login' || tab === 'signup') && (
            <div className="mb-5 flex border-b-2 border-slate-200">
              {(['login', 'signup'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => switchTab(t)}
                  className={`flex-1 py-2.5 text-[13px] font-bold transition-all ${
                    tab === t
                      ? 'border-b-2 border-[#6ebe48] text-slate-900 -mb-[2px]'
                      : 'text-slate-500/70 hover:text-slate-900'
                  }`}
                >
                  {t === 'login' ? 'Sign In' : 'Sign Up'}
                </button>
              ))}
            </div>
          )}

          {/* Back header for forgot/reset */}
          {(tab === 'forgot' || tab === 'reset') && (
            <button
              onClick={() => switchTab('login')}
              className="mb-4 flex items-center gap-1.5 text-[12px] font-bold text-slate-500 hover:text-slate-900 transition"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Sign In
            </button>
          )}

          {/* ── LOGIN FORM ── */}
          {tab === 'login' && (
            <form onSubmit={onLogin} className="space-y-4 animate-fade-in" noValidate>
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.2px] text-slate-500">
                  Email Address
                </label>
                <TextInput
                  type="email"
                  autoComplete="username"
                  inputMode="email"
                  placeholder="name@trc.co.in"
                  error={loginForm.formState.errors.email?.message}
                  {...loginForm.register('email')}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-bold uppercase tracking-[0.2px] text-slate-500">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => switchTab('forgot')}
                    className="text-[11px] font-bold text-slate-900 hover:text-[#074f4d] hover:underline"
                  >
                    Forgot?
                  </button>
                </div>
                <PwInput
                  autoComplete="current-password"
                  placeholder="••••••••"
                  error={loginForm.formState.errors.password?.message}
                  {...loginForm.register('password')}
                />
              </div>

              {formError && (
                <div
                  role="alert"
                  className="rounded-xl border-l-4 border-[#c0392b] bg-[#fde8e8] p-2.5 text-[11.5px] font-bold text-[#991b1b]"
                >
                  {formError}
                </div>
              )}

              <SubmitBtn loading={loginForm.formState.isSubmitting}>
                <KeyRound className="h-4 w-4" /> Sign In
              </SubmitBtn>
            </form>
          )}

          {/* ── SIGN UP FORM ── */}
          {tab === 'signup' && (
            <form onSubmit={onSignup} className="space-y-3.5 animate-fade-in" noValidate>
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.2px] text-slate-500">
                  Full Name
                </label>
                <TextInput
                  placeholder="e.g. Rajan Kumar"
                  error={signupForm.formState.errors.full_name?.message}
                  {...signupForm.register('full_name')}
                />
              </div>

              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.2px] text-slate-500">
                  Email Address
                </label>
                <TextInput
                  type="email"
                  inputMode="email"
                  placeholder="name@trc.co.in"
                  error={signupForm.formState.errors.email?.message}
                  {...signupForm.register('email')}
                />
              </div>

              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.2px] text-slate-500">
                  Password
                </label>
                <PwInput
                  placeholder="Min. 8 characters"
                  error={signupForm.formState.errors.password?.message}
                  {...signupForm.register('password')}
                />
              </div>

              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.2px] text-slate-500">
                  Confirm Password
                </label>
                <PwInput
                  placeholder="Repeat password"
                  error={signupForm.formState.errors.confirm?.message}
                  {...signupForm.register('confirm')}
                />
              </div>

              {formError && (
                <div
                  role="alert"
                  className="rounded-xl border-l-4 border-[#c0392b] bg-[#fde8e8] p-2.5 text-[11.5px] font-bold text-[#991b1b]"
                >
                  {formError}
                </div>
              )}

              <SubmitBtn loading={signupForm.formState.isSubmitting}>
                <User className="h-4 w-4" /> Create Account
              </SubmitBtn>
            </form>
          )}

          {/* ── FORGOT PASSWORD ── */}
          {tab === 'forgot' && (
            <form onSubmit={onForgot} className="space-y-4 animate-fade-in" noValidate>
              <div>
                <p className="mb-3 text-[12.5px] font-medium text-slate-500">
                  Enter your registered work email to receive a password reset token.
                </p>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.2px] text-slate-500">
                  Email Address
                </label>
                <TextInput
                  type="email"
                  inputMode="email"
                  placeholder="name@trc.co.in"
                  error={forgotForm.formState.errors.email?.message}
                  {...forgotForm.register('email')}
                />
              </div>

              {formError && (
                <div
                  role="alert"
                  className="rounded-xl border-l-4 border-[#c0392b] bg-[#fde8e8] p-2.5 text-[11.5px] font-bold text-[#991b1b]"
                >
                  {formError}
                </div>
              )}

              <SubmitBtn loading={forgotForm.formState.isSubmitting}>
                <Mail className="h-4 w-4" /> Send Reset Token
              </SubmitBtn>
            </form>
          )}

          {/* ── RESET PASSWORD ── */}
          {tab === 'reset' && (
            <form onSubmit={onReset} className="space-y-4 animate-fade-in" noValidate>
              {resetDone ? (
                <div className="flex flex-col items-center gap-3 py-4 animate-bounce-in text-center">
                  <CheckCircle2 className="h-12 w-12 text-[#6ebe48]" />
                  <p className="text-[15px] font-bold text-slate-900">Password reset successfully!</p>
                  <p className="text-[12px] text-slate-500">Signing you in…</p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.2px] text-slate-500">
                      Reset Token
                    </label>
                    <TextInput
                      placeholder="Paste token here"
                      error={resetForm.formState.errors.token?.message}
                      {...resetForm.register('token')}
                      defaultValue={resetToken}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.2px] text-slate-500">
                      New Password
                    </label>
                    <PwInput
                      placeholder="Min. 8 characters"
                      error={resetForm.formState.errors.new_password?.message}
                      {...resetForm.register('new_password')}
                    />
                  </div>

                  {formError && (
                    <div
                      role="alert"
                      className="rounded-xl border-l-4 border-[#c0392b] bg-[#fde8e8] p-2.5 text-[11.5px] font-bold text-[#991b1b]"
                    >
                      {formError}
                    </div>
                  )}

                  <SubmitBtn loading={resetForm.formState.isSubmitting}>
                    <KeyRound className="h-4 w-4" /> Set New Password
                  </SubmitBtn>
                </>
              )}
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-[11.5px] text-[#dbf3d0]/60">
          TRC Corporate Consulting Pvt. Ltd. · {new Date().getFullYear()}
        </p>
      </div>
    </div>
  )
}
